<template>
  <main class="modern-scrollbar">

    <!-- ── Header ── -->
    <div class="py-4 container-fluid">
      <div class="section-header-card card">
        <div class="card-body p-0">
          <div class="row gx-4 align-items-center">
            <div class="col-auto my-auto">
              <h5 class="mb-0 header-title">{{ $t("Files.marketServiceTitle") }}</h5>
              <p class="mb-0 text-xs text-muted">{{ $t("Files.secondaryTitle") }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Loading ── -->
    <div class="container-fluid">
      <div v-if="inProgress" class="loading-state">
        <div class="custom-loader">
          <div class="loader-circle"/>
          <div class="loader-circle"/>
          <div class="loader-circle"/>
          <div class="loader-circle"/>
        </div>
        <p class="loading-text">Loading...</p>
      </div>

      <!-- ── Subscribed services ── -->
      <div v-else class="accordion-list">
        <div
            v-for="service in MONTHLY_SERVICES || []"
            :key="service.id || service.subscriptionName"
            class="accordion-service-card"
        >
          <!-- Service header row -->
          <div class="service-header">
            <div class="service-thumbnail">
              <img :src="resolvePublicUrl(service.thumbnail)" alt="" class="thumb-img" />
            </div>

            <div class="service-meta">
              <div class="service-identity">
                <img :src="resolvePublicUrl(service.iconUrl)" class="service-icon" alt="" />
                <span class="service-name">{{ service.subscriptionName }}</span>
              </div>

              <!-- Category columns: always 3 fixed slots -->
              <div class="folder-columns">
                <div
                    v-for="group in groupedFolders(service.folders)"
                    :key="group.label"
                    class="folder-group"
                >
                  <div class="group-label" :class="{ 'group-label--empty': group.folders.length === 0 }">
                    {{ group.label }}
                  </div>
                  <!-- wrap each folder row -->
                  <div v-for="folder in group.folders" :key="folder.folderName" class="folder-wrap">
                    <div
                        class="folder-row"
                        :class="{ 'folder-row--active': isOpen(service, folder), 'folder-row--disabled': folderIsDisabled(folder) }"
                        @click="toggleFolder(service, folder, service.subscriptionName)"
                    >
                      <span class="folder-name">{{ folder.folderName }}</span>
                      <span class="folder-meta">
                        <span v-if="folder.isArchived" class="updated-date">{{ $t("Files.archived") }}</span>
                        <span v-else-if="latestDate(folder)" class="updated-date">Updated {{ latestDate(folder) }}</span>
                        <span v-if="folder.redirectUrl" class="folder-badge folder-badge--link"><i class="fa fa-external-link"></i></span>
                        <i v-else-if="folder.hasFiles" class="chevron-icon fa" :class="isOpen(service, folder) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </span>
                    </div>

                    <!-- inline file panel -->
                    <div v-if="isOpen(service, folder)" class="inline-file-panel">
                      <div class="inline-file-inner">
                        <div v-for="file in openFolder.files" :key="file.id || file.title" class="file-row">
                          <div class="file-info">
                            <span class="file-title">{{ file.title }}</span>
                            <span v-if="file.isArchived" class="updated-date">{{ $t("Files.archived") }}</span>
                          </div>
                          <div v-if="!file.isArchived" class="file-actions">
                            <span v-if="file.startDate" class="file-date">Updated {{ formatFileDate(file.startDate) }}</span>
                            <button
                                class="action-btn action-btn--email"
                                title="Email"
                                @click.stop="sendToEmail(file)"
                                :disabled="emailingFileId === file.id"
                            >
                              <i :class="emailingFileId === file.id ? 'fa fa-spinner fa-spin' : 'fa fa-envelope'"></i>
                            </button>
                            <a @click="viewFile(file)" target="_blank" class="action-btn action-btn--view" title="View"><i class="fa fa-eye"></i></a>
                            <button class="action-btn action-btn--download" title="Download" @click="downloadFile(file)"><i class="fa fa-download"></i></button>
                          </div>
                        </div>
                        <div v-if="!openFolder.files || openFolder.files.length === 0" class="no-files">No files available.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Other Market Analysis header ── -->
    <div class="py-4 container-fluid">
      <div class="section-header-card card">
        <div class="card-body p-0">
          <div class="row gx-4 align-items-center">
            <div class="col-auto my-auto">
              <h5 class="mb-0 header-title">Other Market Analysis</h5>
              <p class="mb-0 text-xs text-muted"><a href="mailto:info@projectblue.com">Contact the team</a> to learn more about our other product offerings.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Unsubscribed services ── -->
    <div class="container-fluid">
      <div v-if="inProgress" class="loading-state">
        <div class="custom-loader">
          <div class="loader-circle"/>
          <div class="loader-circle"/>
          <div class="loader-circle"/>
          <div class="loader-circle"/>
        </div>
        <p class="loading-text">Loading...</p>
      </div>
      <div v-else class="accordion-list accordion-list--unsubscribed">
        <div
            v-for="service in UNSUBSCRIBED_MONTHLY_SERVICES || []"
            :key="service.id || service.subscriptionName"
            class="accordion-service-card accordion-service-card--locked"
        >
          <div class="service-header">
            <div class="service-thumbnail">
              <img :src="resolvePublicUrl(service.thumbnail)" alt="" class="thumb-img" />
              <div class="thumb-lock-overlay"><i class="fa fa-lock"></i></div>
            </div>

            <div class="service-meta">
              <div class="service-identity">
                <img :src="resolvePublicUrl(service.iconUrl)" class="service-icon" alt="" />
                <span class="service-name">{{ service.subscriptionName }}</span>
              </div>

              <div class="folder-columns">
                <div
                    v-for="group in groupedFolders(service.folders)"
                    :key="group.label"
                    class="folder-group"
                >
                  <div class="group-label" :class="{ 'group-label--empty': group.folders.length === 0 }">
                    {{ group.label }}
                  </div>
                  <div
                      v-for="folder in group.folders"
                      :key="folder.folderName"
                      class="folder-row folder-row--disabled"
                  >
                    <span class="folder-name">{{ folder.folderName }}</span>
                    <span class="folder-meta">
                      <span v-if="folder.isArchived" class="updated-date">{{ $t("Files.archived") }}</span>
                      <span v-else-if="latestDate(folder)" class="updated-date">Updated {{ latestDate(folder) }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </main>
</template>

<script setup>
import { sortBy } from "lodash";
import jwtDecode from "jwt-decode";
import { onMounted, ref, computed, onBeforeMount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { ROUTES } from "../../routes/names";
import {
  USER_FILES,
  getUserFiles,
  COST_SERVICES,
  MONTHLY_SERVICES,
  UNSUBSCRIBED_MONTHLY_SERVICES,
  inProgress,
  sendFileToEmail,
  downloadFile as downloadFileComposable,
  visibleFolders,
  folderIsDisabled,
} from "../../composables/files/list";
import { useI18n } from "vue-i18n";
import { saveToUserLogs, userLogInputFields } from "../../composables/userLogs";
import { GQL_QUERIES } from "../../utils/APIs";
import { resolvePublicUrl } from "../../utils/fileUrl";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
let selectedElement = ref(null);
const emailingFileId = ref(null);
let folderName = ref(null);
// ── State ──────────────────────────────────────────────
const openFolder = ref(null); // { serviceId, folderName, files[] }

// ── Helpers ────────────────────────────────────────────
const serviceKey = (service) => service.id || service.subscriptionName;

const isOpen = (service, folder) =>
    openFolder.value?.serviceId === serviceKey(service) &&
    openFolder.value?.folderName === folder.folderName;

const toggleFolder = (service, folder, subscriptionName = null) => {
  if (folder?.isArchived) return;
  if (folder.redirectUrl) {
    window.location.href = folder.redirectUrl;
    return;
  }
  if (!folder.hasFiles) return;

  if (isOpen(service, folder)) {
    openFolder.value = null;
    return;
  }
  selectedElement.value = subscriptionName;
  folderName.value = folder.folderName;
  let fileData = folder.files;
  const files = (fileData || []).filter((f) => !f.isHidden).map((f) => ({
    title: f.fileName,
    type: f.fileType,
    url: f.fileUrl,
    id: f.fileUuid,
    startDate: f.startDate || null,
    isArchived: !!f.isArchived,
  }));

  openFolder.value = {
    serviceId: serviceKey(service),
    folderName: folder.folderName,
    files,
  };
};

const latestDate = (folder) => {
  // Use folder-level startDate first, then fall back to the latest startDate across its files
  if (folder.startDate) {
    const d = new Date(folder.startDate);
    if (!isNaN(d)) return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  if (!folder.files || folder.files.length === 0) return null;
  const dates = folder.files
      .map((f) => f.startDate)
      .filter(Boolean)
      .map((d) => new Date(d))
      .filter((d) => !isNaN(d));
  if (!dates.length) return null;
  const latest = new Date(Math.max(...dates));
  return latest.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Categorise folders into the three column groups shown in the design
const HISTORICAL_KEYWORDS = ["background", "analysis", "trade", "producer", "history"];
const FORECAST_KEYWORDS = ["short-term", "medium-term", "long-term", "outlook", "forecast"];
// everything else goes to DATA

const folderCategory = (folderName) => {
  const lower = folderName.toLowerCase();
  if (HISTORICAL_KEYWORDS.some((k) => lower.includes(k))) return "HISTORICAL ANALYSIS";
  if (FORECAST_KEYWORDS.some((k) => lower.includes(k))) return "FORECASTS";
  return "DATA";
};

const groupedFolders = (folders = []) => {
  const groups = {
    "HISTORICAL ANALYSIS": [],
    FORECASTS: [],
    DATA: [],
  };
  visibleFolders(folders).forEach((f) => groups[folderCategory(f.folderName)].push(f));
  // Always return all three columns — empty ones hold their grid slot
  return Object.entries(groups).map(([label, flds]) => ({ label, folders: flds }));
};

const formatFileDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const shortenFileName = (name, len = 20) =>
    name.length > len ? name.substring(0, len) + "…" : name;

// ── Actions ────────────────────────────────────────────
const viewFile = async (file) => {
  console.log("viewFile", file);
  const fileExtension = file.url?.split('.').pop().split('?')[0].toLowerCase();
  if (fileExtension === "xls" || fileExtension === "xlsx") {
    window.open(`https://docs.google.com/viewer?embedded=true&url=${file.url}`);
    return;
  }

  // Open the file immediately — don't let logging block or break it
  window.open(file.url);

  // Log in the background, silently catch errors
  try {
    userLogInputFields.value = {
      userEmail: localStorage.getItem('email'),
      description: `User ${localStorage.getItem('email')} viewed ${file.url}.`,
      action: "ViewFile",
      method: null,
      statusCode: 200,
      environment: window.location.hostname,
      endpoint: null,
      path: null,
    };
    await saveToUserLogs(userLogInputFields.value);
  } catch (e) {
    console.warn("User log failed:", e);
  }
};

const sendToEmail = async (file) => {
  emailingFileId.value = file.id;
  try {
    await sendFileToEmail(file.id);
  } finally {
    emailingFileId.value = null;
  }
};

const downloadFile = async (file) => {
  await downloadFileComposable(file.id, selectedElement.value, openFolder.value?.folderName);
};

// ── Lifecycle ──────────────────────────────────────────
onBeforeMount(() => window.$loading.start());

onMounted(async () => {
  if (!USER_FILES.value?.files) await getUserFiles();

  await new Promise((resolve) => {
    const check = () =>
        MONTHLY_SERVICES.value?.length > 0 ? resolve() : setTimeout(check, 100);
    check();
  });

  if (route.query.element) {
    if (route.query.folder && route.query.autoOpen === "true") {
      setTimeout(() => {
        const service = MONTHLY_SERVICES.value.find(
            (s) =>
                s.element === route.query.element ||
                s.id === route.query.element ||
                s.subscriptionName?.toLowerCase().includes(route.query.element.toLowerCase())
        );
        if (service) {
          const folder = service.folders.find((f) => f.folderName === route.query.folder);
          if (folder) toggleFolder(service, folder);
        }
      }, 300);
    } else {
      const result = MONTHLY_SERVICES.value.find((x) => x.id == route.query.element);
      if (result) {
        const first = (result.folders || []).find((f) => f.hasFiles);
        if (first) toggleFolder(result, first);
      }
    }
  }

  if (USER_FILES.value?.files) window.$loading.finish();
});
</script>

<style lang="scss" scoped>
// ── Design tokens ──────────────────────────────────────
$navy:       #00213b;
$navy-light: #00213b;
$text:       #213ce5;
$black-text: #000;
$text-muted: #718096;
$border:     #e4e9f0;
$bg-row:     #f8f9fa;
$radius:     0;

// ── Section header card ────────────────────────────────
.section-header-card {
  border: none;
  border-radius: $radius;
  background: #fff;

  .header-title {
    font-size: 1rem;
    font-weight: 700;
    color: #000;
  }
}
.card {
  box-shadow: none;
}
// ── Accordion list ─────────────────────────────────────
.accordion-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 16px;
}

// ── Service card ───────────────────────────────────────
.accordion-service-card {
  background: $bg-row;
  overflow: hidden;
  &:hover {
    box-shadow: 0 2px 12px rgba($navy, 0.08);
  }

  &--locked {
    opacity: 0.75;
    pointer-events: none;

    .service-name { color: $text-muted; }
  }
}

// ── Service header row ─────────────────────────────────
.service-header {
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.service-thumbnail {
  position: relative;
  align-self: flex-start;
  flex: 0 0 143px;
  min-height: 65px;
  max-height: 143px;

  .thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .thumb-lock-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($navy, 0.45);
    color: #fff;
    font-size: 1.2rem;
  }
}

.service-meta {
  flex: 1;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-identity {
  display: none;
  align-items: center;
  gap: 8px;

  .service-icon {
    width: 26px;
    height: 26px;
    border-radius: 4px;
    object-fit: contain;
  }

  .service-name {
    font-weight: 700;
    font-size: 0.95rem;
    color: $navy;
  }
}

// ── Folder column groups ───────────────────────────────
.folder-columns {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0 24px;
}

.folder-group {
  min-width: 0; // prevent grid blowout
}

.group-label {
  font-size: 0.8125rem;
  font-weight: 400;
  letter-spacing: 0.125rem;
  text-transform: uppercase;
  color: $text-muted;
  margin-bottom: 4px;
  padding-bottom: 3px;
  border-bottom: 1px solid $border;

  &--empty {
    visibility: hidden; // holds layout slot, shows nothing
  }
}

// ── Individual folder row ──────────────────────────────
.folder-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.2rem 0;
  margin-bottom: 2px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  gap: 8px;

  &:hover:not(.folder-row--disabled) {
    .folder-name,.updated-date {
      color: $text;
      text-decoration: underline;
    }
  }

  &--active {
    background: $bg-row !important;

    .folder-name,
    .updated-date{
      color: $text !important;
      text-decoration: underline;
      //&.fa-chevron-up {
      //  color: #213ce5 !important; /* up state */
      //}
    }
  }

  &--disabled {
    cursor: default;
    opacity: 0.55;
  }

  .folder-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #000;
    white-space: nowrap;
  }

  .folder-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .updated-date {
    font-size: 0.68rem;
    color: $text-muted;
    white-space: nowrap;
  }

  .chevron-icon {
    font-size: 0.7rem;
    color: $text-muted;
    transition: transform 0.2s;
  }

  .folder-badge--link {
    font-size: 0.7rem;
    color: $navy;
  }
}

// ── Expanded file panel ────────────────────────────────
.file-list-panel {
  background: $bg-row;
}

.file-list-inner {
  padding: 6px 14px 10px;
}

.file-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;

  & + .file-row {
    border-top: 1px solid $border;
  }

  &:hover {
    background: rgba($navy, 0.03);
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1 1 0;
    min-width: 0;

    .file-type-icon {
      font-size: 1rem;
      color: $navy;
      flex-shrink: 0;
    }

    .file-title {
      font-size: 0.8125rem;
      font-weight: 500;
      color: $black-text;
      word-break: break-word;
      white-space: normal;
    }
  }

  .file-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    margin-left: auto;
    align-self: flex-start;

    .file-date {
      font-size: 0.68rem;
      color: $text-muted;
      white-space: nowrap;
      margin: 2px 4px 0 0;
      flex-shrink: 0;
    }
  }
}

// ── Action buttons ────────────────────────────────────
.action-btn {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: filter 0.15s, transform 0.1s;
  flex-shrink: 0;
  border: 1px solid transparent;

  &:active { transform: scale(0.97); }

  i { font-size: 0.5rem; }
  &:hover {
    border: 1px solid #00aeef !important;
  }
  &--view { background: linear-gradient(90deg, #213ce5, #2380f2); color: #fff; &:hover { filter: brightness(1.08); color: #fff; } }
  &--email { background: linear-gradient(90deg, #213ce5, #2380f2); color: #fff; &:hover { filter: brightness(1.08); } &:disabled { background: transparent; pointer-events: none; i { color: #213ce5; font-size: 0.75rem; }}}
  &--download { background: linear-gradient(90deg, #213ce5, #2380f2); color: #fff; &:hover { filter: brightness(1.08); } }
}

.no-files {
  font-size: 0.8rem;
  color: $text-muted;
  padding: 10px 4px;
}

// ── Dates hidden (kept in DOM, just not shown) ─────────
.updated-date,
.file-date {
  display: none !important;
}

// ── Responsive ────────────────────────────────────────
@media (max-width: 1250px) {
  .updated-date {
    display: none;
  }
  .file-date {
    display: none;
  }
}

@media (max-width: 700px) {
  .folder-columns {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 640px) {
  .service-thumbnail { display: none; }
  .service-identity { display: flex; }
  .updated-date { display: none; };
  .folder-columns { grid-template-columns: 1fr; gap: 12px; }
  .folder-group { min-width: 120px; }
  .action-btn span { display: none; }
  .action-btn { padding: 5px 8px; }
}
@media (max-width: 480px) {
  .file-date { display: none; }
}
// ── Loading state ──────────────────────────────────────
.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
  gap: 1.5rem;
}

.custom-loader {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.loader-circle {
  width: 12px;
  height: 12px;
  background-color: $navy;
  border-radius: 50%;
  animation: loader-bounce 1.4s ease-in-out infinite both;

  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
  &:nth-child(3) { animation-delay: 0s; }
  &:nth-child(4) { animation-delay: 0.16s; }
}

@keyframes loader-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
  40%           { transform: scale(1); opacity: 1; }
}

.loading-text {
  color: $navy;
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  opacity: 0.8;
}
</style>